import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import NewHeader from "../Header/NewHeader";
import NewFooter from "../Footer/NewFooter";
import Typewriter from "typewriter-effect";

gsap.registerPlugin(ScrollTrigger);

const OurPhilosophy = () => {
  const sectionsRef = useRef([]);

  const addToRefs = (el) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  useEffect(() => {
    sectionsRef.current.forEach((section) => {
      // Animate texts
      const texts = section.querySelectorAll("p, h2");
      gsap.fromTo(
        texts,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: 0.4,
          ease: "power2.out",
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            toggleActions: "play none none none",
          },
        }
      );

      const images = section.querySelectorAll("img");
      images.forEach((img) => {
        gsap.fromTo(
          img,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: img,
              start: "top 85%",
              toggleActions: "play none none none",
            },
          }
        );
      });
    });
  }, []);

  return (
    <div className="relative w-full bg-[#f5f2f0] overflow-x-hidden scroll-smooth">
      {/* ---- Desktop View ---- */}
      <div className="hidden lg:block overflow-y-auto overflow-x-hidden">
        {/* ---- Section 1 ---- */}
        <div
          className="fixed top-0 left-0 w-screen h-screen bg-cover bg-center z-0"
          style={{
            backgroundImage: `url('https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/cf7e7b2c-2788-41f5-86a3-2a810fed7a00/public')`,
          }}
        >
          <div className="absolute inset-0 bg-black/50"></div>

          <div className="absolute inset-0 flex items-center justify-center">
            <p
              className="text-[14px] tracking-[0.25em] uppercase text-center text-[#F5F2F0] whitespace-nowrap overflow-hidden border-r-2 border-[#F5F2F0]"
              style={{
                width: "0ch",
                animation:
                  "typing 3s steps(24) forwards, blink .8s step-end 3s",
                animationFillMode: "forwards",
              }}
            >
              Lose yourself feel found
            </p>
          </div>
          <style jsx>{`
            @keyframes typing {
              from {
                width: 0;
              }
              to {
                width: 44ch;
              }
            }

            @keyframes blink {
              50% {
                border-color: transparent;
              }
              100% {
                border-color: transparent;
              }
            }
          `}</style>
        </div>

        <div className="relative z-10 pt-[100vh]">
          <NewHeader />

          {/* ---- Section 2 ---- */}
          <section
            ref={addToRefs}
            className="relative w-screen min-h-screen flex flex-col lg:flex-row justify-between items-center bg-[#f5f2f0] overflow-x-hidden 2xl:-mt-1 xl:-mt-1"
          >
            <div className="w-full flex flex-col lg:flex-row justify-between items-center px-8 md:px-16 lg:px-20 xl:px-32 2xl:px-[12vw] py-24 lg:py-36 text-[#948A78]">
              <div className="flex-1 flex flex-col justify-center space-y-8 lg:space-y-10 xl:space-y-8 ">
                <div className="leading-relaxed text-[13px] space-y-4 lg:ml-5 xl:ml-20 2xl:ml-28 lg:w-[350px] xl:w-[400px] 2xl:w-[500px] relative -top-6 lg:-top-10 xl:-top-14 2xl:-top-28">
                  <p>
                    At House of Tāo, hospitality is not just about where you
                    stay — it’s about how you feel.
                  </p>
                  <p>
                    This is more than a villa; it’s an intentional space that
                    lets you slow down, breathe deeply, and connect with what
                    matters.
                  </p>
                  <p>
                    Every corner carries a story — from the locally sourced
                    materials to the soft, grounding textures that make you feel
                    at home the moment you arrive.
                  </p>
                </div>

                <div className="flex flex-col items-end text-right">
                  <h2 className="text-[20px] tracking-wide mb-2 lg:mr-52 xl:mr-40 2xl:mr-24 w-[100px]">
                    Living the <br /> Tao Life
                  </h2>
                  <div className="text-left leading-relaxed text-[13px] space-y-3 lg:-mr-20 xl:-mr-40 2xl:-mr-20 lg:w-[300px] 2xl:w-[200px] xl:w-[300px]">
                    <p>Choose ease over effort, presence over pace.</p>
                    <p>
                      Let the day unfold without urgency, rise with the sun,
                      walk barefoot, listen to the wind move through the trees.
                    </p>
                    <p>
                      Nothing here demands your attention, yet everything
                      invites it. In this quiet rhythm, you begin to notice more
                      — not just around you, but within you.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex-1 flex justify-end items-center mt-16 lg:mt-0">
                <img
                  src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/0eaf71e3-77d4-4eb8-bee5-e10cd567a700/public" //Tao_ourPhilosophy_1.webp
                  alt="Living the Tao Life"
                  className="w-[60%] max-w-[500px] xl:max-w-[580px] 2xl:max-w-[700px] h-auto object-cover object-center"
                />
              </div>
            </div>
          </section>

          {/* ---- Section 3 ---- */}
          <section className="relative w-screen h-screen overflow-hidden bg-[#f5f2f0]">
            <img
              src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/60fbafe1-a470-41d4-e20d-2a333e043100/public" //Tao_ourPhilosophy_2.webp
              alt="Harmony with Nature"
              className="w-full h-full object-cover object-center"
            />

            <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-[#948A78] text-[13px] tracking-[0.25em] uppercase">
              in silence, everything speaks
            </div>
          </section>

          {/* section 4 */}
          <section
            ref={addToRefs}
            className="w-screen min-h-screen flex flex-col lg:flex-row bg-[#f5f2f0] overflow-hidden"
          >
            <div className="flex-1 flex justify-center items-center">
              <img
                src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/dcb5080d-643e-44c1-b09c-6e6b0e678100/public" //Tao_ourPhilosophy_3.webp
                alt="Minimalist cream-colored interior with arched doorways and wooden staircase"
                className="w-[55vh] h-[82vh] object-cover object-center lg:ml-10 xl:ml-32"
              />
            </div>

            <div className="flex-1 flex flex-col justify-center items-center text-center px-8 lg:px-16 xl:px-24 space-y-10 text-[#948A78]">
              <div className="flex justify-center">
                <img
                  src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/f760065d-4b4a-48e7-31ad-42592c8a2400/public" //Tao_ourPhilosophy_4.webp
                  alt="Close-up of a circular window with flowing curtains and a small table"
                  className="w-[48vh] h-[35vh] object-cover object-center mb-14 2xl:mb-32 xl:ml-20 lg:ml-24"
                />
              </div>

              <div className="flex flex-col items-start  2xl:mr-80">
                <h2 className="text-[20px] tracking-wide mb-2 text-right  -ml-36">
                  The Tao <br /> State of Mind
                </h2>
                <div className="text-left leading-relaxed text-[13px] space-y-3 w-[250px]">
                  <p>More than a stay — a sensory pause. </p>
                  <p>A home to feel restored, grounded, and present.</p>
                </div>
              </div>
            </div>
          </section>
          {/* <section className="w-screen min-h-screen flex flex-col lg:flex-row bg-[#f5f2f0] overflow-hidden">
            
            <div className="flex-1 flex justify-center items-center">
              <img
                src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/dcb5080d-643e-44c1-b09c-6e6b0e678100/public" //Tao_ourPhilosophy_3.webp
                alt="Minimalist cream-colored interior with arched doorways and wooden staircase"
                className="w-[55vh] h-[82vh] object-cover object-center lg:ml-14 xl:ml-32"
              />
            </div>

            <div className="flex-1 flex flex-col justify-center items-center text-center px-8 lg:px-16 xl:px-24 space-y-10 text-[#948A78]">
             
              <div className="flex justify-center">
                <img
                  src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/f760065d-4b4a-48e7-31ad-42592c8a2400/public" //Tao_ourPhilosophy_4.webp
                  alt="Close-up of a circular window with flowing curtains and a small table"
                  className="w-[48vh] h-[35vh] object-cover object-center mb-14 2xl:mb-32 xl:ml-20"
                />
              </div>

              <div className="flex flex-col items-end text-right">
                <h2 className="text-[20px] tracking-wide mb-2 lg:mr-[55vh] xl:mr-[85vh] 2xl:mr-[70vh] w-[200px]">
                  The Tao <br /> State of Mind
                </h2>
                <div className="text-left leading-relaxed text-[13px] space-y-3 lg:mr-32 xl:mr-[45vh] 2xl:mr-[50vh] lg:w-[200px] 2xl:w-[250px] xl:w-[250px]">
                  <p>More than a stay — a sensory pause. </p>
                  <p>A home to feel restored, grounded, and present.</p>
                </div>
              </div>
            </div>
          </section> */}

          {/* section 5 */}
          <section
            ref={addToRefs}
            className="meet-host-section-container w-full min-h-screen bg-[#f5f2f0] -mt-10 flex flex-col items-end justify-center px-6 py-16 lg:px-20 text-[#948A78]"
          >
            <div className="justify-center xl:mr-[70vh] lg:mr-[60vh]">
              <h2 className="text-[13px] mb-12 w-full lg:w-auto text-left xl:mr-60 lg:mr-44 2xl:mr-[35vh] 4k:mr-[15vh] tracking-[0.25em]">
                MEET YOUR HOST
              </h2>
            </div>

            <div className="hosts-profiles-wrapper flex flex-col lg:flex-row items-end justify-end gap-14 w-full max-w-6xl xl:mr-20 2xl:mr-40">
              <div className="host-profile flex flex-col items-end text-right space-y-6">
                <div className="host-image-container overflow-hidden">
                  <img
                    src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/99f8a947-80d5-4cdc-159b-e69b04dbdc00/public"
                    alt="Parth Thaker"
                    className="host-image xl:w-[55vh] xl:h-[35vh] 2xl:w-[60vh] 2xl:h-[40vh] lg:w-[55vh] lg:h-[35vh] object-cover"
                  />
                </div>
                <div className="space-y-2 text-[13px] w-[300px] lg:w-[300px] text-left xl:-mr-6 lg:-mr-6 2xl:-mr-5">
                  <p>
                    Parth Thaker and Vimal started with just two apartments in
                    Goa back in 2017, driven by a shared love for Goa and
                    everything it offers.
                  </p>
                  <p>
                    Parth brings a deep passion for hospitality, always looking
                    for ways to make every guest feel at home.
                  </p>
                </div>
              </div>

              <div className="host-profile flex flex-col items-end text-right space-y-6">
                <div className="host-image-container overflow-hidden">
                  <img
                    src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/ec1cccbd-2d30-47ec-11d4-d7132f159600/public"
                    alt="Vimal smiling with arms crossed"
                    className="host-image w-[55vh] h-[35vh] 2xl:w-[60vh] 2xl:h-[40vh] lg:w-[55vh] lg:h-[35vh] object-cover"
                  />
                </div>
                <p className="text-[13px] py-1 w-[270px] text-left xl:-mr-2 lg:-mr-3 2xl:-mr-1">
                  Vimal, with his eye for detail and marketing skills, brings
                  Goa to people long before they arrive. Together, they’ve grown
                  Soham Villas with care, creating spaces that feel personal,
                  peaceful and true to the spirit of Goa.
                </p>
              </div>
            </div>
          </section>

          {/* section 6 */}
          <section
            ref={addToRefs}
            className="soham-villas-container -mt-10 py-16 px-6 bg-[#f5f2f0] text-[#948A78]"
          >
            <div className="soham-villas-main-content flex flex-col lg:flex-row items-stretch gap-12 4k:gap-[18rem] max-w-6xl mx-auto">
              <div className="flex-1 flex flex-col justify-between items-start xl:ml-5 lg:ml-10 4k:-ml-[20vh] space-y-4">
                <div className="text-left w-[350px] xl:w-[370px]">
                  <p className="text-[13px]">
                    House of Tāo is brought to you by Soham Villas, a boutique
                    hospitality brand known for its design-forward stays and
                    thoughtful guest experiences. Inspired by the quiet beauty
                    of Parra village, this villa was envisioned as a sanctuary
                    where time moves at your pace.
                  </p>
                </div>

                <div>
                  <img
                    src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/9312407f-7eba-4d43-c4ec-5fcea0005b00/public" //Tao_ourPhilosophy_7.webp
                    alt="Soham Villas"
                    className="w-[50vh] h-[78vh] object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 flex flex-col justify-end xl:-mb-12 lg:-mb-10">
                <h2 className="text-[20px] xl:-ml-28 lg:-ml-20 2xl:-ml-5 mb-6">
                  About Soham Villas
                </h2>

                <div className="text-[13px] xl:w-[350px] xl:ml-20 lg:ml-10 lg:w-[400px] space-y-4 mb-6">
                  <p>
                    Just like you - we came to Goa, but could never leave. Since
                    then it’s the journey of making your Goa experience as
                    holistic as it was ours.
                  </p>
                  <p>
                    These detoxing experiences led us to create something
                    permanent here - what first began with a few apartments has
                    now developed elegantly into a collection of carefully
                    crafted villas all across the breathtaking landscapes of
                    Goa.
                  </p>
                  <p>
                    We take immense pride and joy for all the exceptional
                    memories we could be a part of, in the course of the last 5
                    years.
                  </p>
                  <p>
                    House of Tao is one of our most personal projects yet,
                    rooted in simplicity, nature and slow living. It’s built not
                    just to stay, but to feel. And we’re grateful to share that
                    feeling with you.
                  </p>
                </div>

                <a
                  href="/BookNow"
                  className="px-6 py-12 xl:ml-14 text-[13px] hover:font-bold underline decoration-[#948A78] underline-offset-[10px] decoration-1 hover:underline-offset-[5px] transition-all duration-300 inline-block text-[#948A78]"
                >
                  BOOK YOUR STAY
                </a>
              </div>
            </div>
          </section>
          <NewFooter />
        </div>
      </div>

      {/* =============== MOBILE VIEW ============== */}
      <div className="flex flex-col lg:hidden text-center relative overflow-x-hidden bg-[#f5f2f0]">
        {/* ---- Section 1 ---- */}
        <div
          className="fixed inset-0 w-full h-full bg-cover bg-center z-0"
          style={{
            backgroundImage: `url('https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/cf7e7b2c-2788-41f5-86a3-2a810fed7a00/public')`,
          }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-[14px] tracking-[0.25em] uppercase text-[#F5F2F0] leading-relaxed">
            <Typewriter
              options={{
                delay: 60,
                deleteSpeed: 30,
              }}
              onInit={(typewriter) => {
                typewriter
                  .typeString("Lose yourself")
                  .pauseFor(800)
                  .typeString("<br/>feel found")
                  .callFunction(() => {
                    const cursor = document.querySelector(
                      ".Typewriter__cursor"
                    );
                    if (cursor) cursor.style.display = "none";
                  })
                  .start();
              }}
            />
          </div>
        </div>

        <div className="relative z-10 pt-[100vh]">
          {/* header */}
          <NewHeader />
          {/* ---- Section 2 ---- */}
          <section
            ref={addToRefs}
            className="w-full min-h-[100vh] flex flex-col items-center justify-center py-28 space-y-8 px-6 bg-[#f5f2f0]"
          >
            <div className="text-left text-[13px] space-y-3 text-[#948A78] ml-20 sm:ml-[50vh] sm:w-[250px]">
              <p>
                At House of Tāo, hospitality is not just about where you stay —
                it’s about how you feel.
              </p>
              <p>
                This is more than a villa; it’s an intentional space that lets
                you slow down, breathe deeply, and connect with what matters.
              </p>
              <p>
                Every corner carries a story — from the locally sourced
                materials to the soft, grounding textures that make you feel at
                home the moment you arrive.
              </p>
            </div>

            <img
              src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/0eaf71e3-77d4-4eb8-bee5-e10cd567a700/public" //Tao_ourPhilosophy_1.webp
              alt="Living the Tao Life"
              className="w-[35vh] h-[52vh] sm:w-[39vh] sm:h-[50vh] object-cover sm:mr-64 mr-24"
            />

            <div className="flex flex-col items-end text-[#948A78] mt-5 ml-[20vh] w-full">
              <h2 className="text-[20px] tracking-wide text-right mb-2 mr-56 sm:mr-80 w-[100px]">
                Living the <br />
                Tao Life
              </h2>

              <div className="leading-relaxed text-[13px] space-y-3 text-left sm:w-[200px] w-[200px] mr-20 sm:mr-32">
                <p>Choose ease over effort, presence over pace.</p>
                <p>
                  Let the day unfold without urgency, rise with the sun, walk
                  barefoot, listen to the wind move through the trees.
                </p>
                <p>
                  Nothing here demands your attention, yet everything invites
                  it. In this quiet rhythm, you begin to notice more, not just
                  around you, but within you.
                </p>
              </div>

              <div className="mt-5 -mr-1 sm:-mr-44 w-full flex justify-center">
                <img
                  src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/f760065d-4b4a-48e7-31ad-42592c8a2400/public" //Tao_ourPhilosophy_4.webp
                  alt="Nature View 2"
                  className="w-[41vh] sm:w-[50vh] object-cover"
                />
              </div>
            </div>
          </section>
          {/* ---- Section 3 ---- */}
          <section
            ref={addToRefs}
            className="relative w-screen h-[60vh] overflow-hidden bg-[#f5f2f0]"
          >
            <img
              src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/dcb5080d-643e-44c1-b09c-6e6b0e678100/public"
              alt="Nature View 3"
              className="sm:w-[50vh] sm:h-[65vh] h-[60vh] w-[40vh] object-cover object-center ml-3 sm:ml-10"
            />
          </section>
          {/* ---- Spacer ---- */}
          <div className="w-full h-20 bg-[#f5f2f0]"></div> {/* visible space */}
          {/* ---- Section 4 ---- */}
          <section
            ref={addToRefs}
            className="hosts-section py-5 px-6 bg-[#f5f2f0] text-[#948A78]"
          >
            <h2 className="section-title text-[13px] text-left sm:ml-3 ml-3 mb-6 uppercase tracking-[0.25em]">
              Meet Your Hosts
            </h2>

            <p className="text-[13px] text-left w-[250px] sm:ml-3 ml-3 mb-12">
              Parth Thaker and Vimal started with just two apartments in Goa
              back in 2017, driven by a shared love for Goa and everything it
              offers.
            </p>

            <div className="hosts-profiles flex flex-col lg:flex-row gap-12 justify-center items-start text-[13px]">
              {/* Parth */}
              <div className="host-profile flex-1 items-end">
                <img
                  src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/99f8a947-80d5-4cdc-159b-e69b04dbdc00/public"
                  alt="Parth Thaker"
                  className="host-image sm:w-[33vh] sm:h-[42vh] sm:ml-[60vh] w-[33vh] h-[42vh] ml-[14vh] object-cover mx-auto lg:mx-0 mb-4"
                />
                <p className="host-description text-left sm:w-[300px] sm:ml-[60vh] mt-8 ml-20">
                  Parth brings a deep passion for hospitality, always looking
                  for ways to make every guest feel at home.
                </p>
              </div>

              {/* Vimal */}
              <div className="host-profile flex-1 items-end">
                <img
                  src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/ec1cccbd-2d30-47ec-11d4-d7132f159600/public"
                  alt="Vimal"
                  className="host-image sm:w-[33vh] sm:h-[42vh] sm:ml-[60vh] w-[33vh] h-[42vh] ml-[14vh] object-cover mx-auto lg:mx-0 mb-4"
                />
                <p className="host-description text-left sm:w-[300px] sm:ml-[60vh] mt-8 ml-20">
                  Vimal, with his eye for detail and marketing skills, brings
                  Goa to people long before they arrive. Together, they’ve grown
                  Soham Villas with care, creating spaces that feel personal,
                  peaceful and true to the spirit of Goa.
                </p>
              </div>
            </div>
          </section>
          {/* section 5 */}
          <section
            ref={addToRefs}
            className="relative w-full bg-[#f5f2f0] py-20 flex flex-col lg:flex-row justify-between items-start px-5 lg:px-20"
          >
            {/* Left Column - Host Profile */}
            <div className="host-profile flex-1 flex flex-col items-start text-[#948A78] text-[13px]">
              <p className="host-description text-left sm:w-[400px] sm:ml-[10vh] mt-8 mb-5 ml-5 mr-20">
                House of Tāo is brought to you by Soham Villas, a boutique
                hospitality brand known for its design-forward stays and
                thoughtful guest experiences. Inspired by the quiet beauty of
                Parra village, this villa was envisioned as a sanctuary where
                time moves at your pace.
              </p>

              <img
                src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/9312407f-7eba-4d43-c4ec-5fcea0005b00/public" // Tao_ourPhilosophy_7.webp
                alt="about"
                className="host-image sm:w-[40vh] sm:h-[62vh] sm:ml-[10vh] w-[35vh] h-[50vh] ml-[4vh] object-cover mx-auto lg:mx-0 mb-4"
              />
            </div>

            {/* Right Column - About Soham Villas */}
            <div className="flex-1 flex flex-col items-start lg:items-end text-[#948A78]">
              <h2 className="text-right sm:ml-[30vh] -ml-14 mt-10 mb-5 text-[#948A78] text-[20px] w-[200px]">
                About <br />
                Soham Villas
              </h2>

              <div className="text-left sm:w-[350px] w-[200px] ml-[12vh] sm:space-y-5 space-y-3 text-[13px] sm:ml-[63vh]">
                <p>
                  Just like you - we came to Goa, but could never leave. Since
                  then it’s the journey of making your Goa experience as
                  holistic as it was ours.
                </p>

                <p>
                  These detoxing experiences led us to create something
                  permanent here - what first began with a few apartments has
                  now developed elegantly into a collection of carefully crafted
                  villas all across the breathtaking landscapes of Goa.
                </p>

                <p>
                  We take immense pride and joy for all the exceptional memories
                  we could be a part of, in the course of the last 5 years.
                </p>

                <p>
                  House of Tao is one of our most personal projects yet, rooted
                  in simplicity, nature and slow living. It’s built not just to
                  stay, but to feel. And we’re grateful to share that feeling
                  with you.
                </p>

                <a
                  href="/BookNow"
                  className="px-6 py-2 -ml-5 text-[13px] hover:font-bold underline decoration-[#948A78] underline-offset-[10px] decoration-1 hover:underline-offset-[5px] transition-all duration-300 mb-6 inline-block text-[#948A78]"
                >
                  BOOK&nbsp;YOUR&nbsp;STAY
                </a>
              </div>
            </div>
          </section>
          <NewFooter />
        </div>
      </div>
    </div>
  );
};

export default OurPhilosophy;

// import React, { useEffect, useRef } from "react";
// import gsap from "gsap";
// import { ScrollTrigger } from "gsap/ScrollTrigger";
// import NewHeader from "../Header/NewHeader";
// import NewFooter from "../Footer/NewFooter";
// import Typewriter from "typewriter-effect";

// gsap.registerPlugin(ScrollTrigger);

// const OurPhilosophy = () => {
//   const sectionsRef = useRef([]);

//   const addToRefs = (el) => {
//     if (el && !sectionsRef.current.includes(el)) {
//       sectionsRef.current.push(el);
//     }
//   };

//   useEffect(() => {
//     sectionsRef.current.forEach((section) => {
//       // Animate texts
//       const texts = section.querySelectorAll("p, h2");
//       gsap.fromTo(
//         texts,
//         { opacity: 0, y: 50 },
//         {
//           opacity: 1,
//           y: 0,
//           duration: 1,
//           stagger: 0.4,
//           ease: "power2.out",
//           scrollTrigger: {
//             trigger: section,
//             start: "top 80%",
//             toggleActions: "play none none none",
//           },
//         }
//       );

//       const images = section.querySelectorAll("img");
//       images.forEach((img) => {
//         gsap.fromTo(
//           img,
//           { opacity: 0, y: 50 },
//           {
//             opacity: 1,
//             y: 0,
//             duration: 1.2,
//             ease: "power2.out",
//             scrollTrigger: {
//               trigger: img,
//               start: "top 85%",
//               toggleActions: "play none none none",
//             },
//           }
//         );
//       });
//     });
//   }, []);

//   return (
//     <div className="relative w-full bg-[#f5f2f0] overflow-x-hidden scroll-smooth">
//       {/* ---- Desktop View ---- */}
//       <div className="hidden lg:block overflow-y-auto overflow-x-hidden">
//         {/* ---- Section 1 ---- */}
//         <div
//           className="fixed top-0 left-0 w-screen h-screen bg-cover bg-center z-0"
//           style={{
//             backgroundImage: `url('https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/cf7e7b2c-2788-41f5-86a3-2a810fed7a00/public')`,
//           }}
//         >
//           <div className="absolute inset-0 bg-black/50"></div>

//           <div className="absolute inset-0 flex items-center justify-center">
//             <p
//               className="text-[14px] tracking-[0.25em] uppercase text-[#F5F2F0] whitespace-nowrap overflow-hidden border-r-2 border-[#F5F2F0]"
//               style={{
//                 width: "0ch",
//                 animation:
//                   "typing 2s steps(35) forwards, blink 2s steps(1) forwards",
//               }}
//             >
//               get lost. feel found.
//             </p>
//           </div>
//           <style jsx>{`
//             @keyframes typing {
//               from {
//                 width: 0;
//               }
//               to {
//                 width: 28ch;
//               }
//             }
//             @keyframes blink {
//               0%,
//               99% {
//                 border-color: #f5f2f0;
//               }
//               100% {
//                 border-color: transparent;
//               }
//             }
//           `}</style>
//         </div>

//         <div className="relative z-10 pt-[100vh]">
//           <NewHeader />

//           {/* ---- Section 2 ---- */}
//           <section
//             ref={addToRefs}
//             className="relative w-screen min-h-screen flex flex-col lg:flex-row justify-between items-center bg-[#f5f2f0] overflow-x-hidden 2xl:-mt-1 xl:-mt-1"
//           >
//             <div className="w-full flex flex-col lg:flex-row justify-between items-center px-8 md:px-16 lg:px-20 xl:px-32 2xl:px-[12vw] py-24 lg:py-36 text-[#948A78]">
//               <div className="flex-1 flex flex-col justify-center space-y-8 lg:space-y-10 xl:space-y-8 ">
//                 <div className="leading-relaxed text-[13px] space-y-4 lg:ml-5 xl:ml-20 2xl:ml-28 lg:w-[350px] xl:w-[400px] 2xl:w-[500px] relative -top-6 lg:-top-10 xl:-top-14 2xl:-top-28">
//                   <p>
//                     At House of Tāo, hospitality is not just about where you
//                     stay — it’s about how you feel.
//                   </p>
//                   <p>
//                     This is more than a villa; it’s an intentional space that
//                     lets you slow down, breathe deeply, and connect with what
//                     matters.
//                   </p>
//                   <p>
//                     Every corner carries a story — from the locally sourced
//                     materials to the soft, grounding textures that make you feel
//                     at home the moment you arrive.
//                   </p>
//                 </div>

//                 <div className="flex flex-col items-end text-right">
//                   <h2 className="text-[20px] tracking-wide mb-2 lg:mr-52 xl:mr-40 2xl:mr-24 w-[100px]">
//                     Living the <br /> Tao Life
//                   </h2>
//                   <div className="text-left leading-relaxed text-[13px] space-y-3 lg:-mr-20 xl:-mr-40 2xl:-mr-20 lg:w-[300px] 2xl:w-[200px] xl:w-[300px]">
//                     <p>Choose ease over effort, presence over pace.</p>
//                     <p>
//                       Let the day unfold without urgency, rise with the sun,
//                       walk barefoot, listen to the wind move through the trees.
//                     </p>
//                     <p>
//                       Nothing here demands your attention, yet everything
//                       invites it. In this quiet rhythm, you begin to notice more
//                       — not just around you, but within you.
//                     </p>
//                   </div>
//                 </div>
//               </div>

//               <div className="flex-1 flex justify-end items-center mt-16 lg:mt-0">
//                 <img
//                   src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/0eaf71e3-77d4-4eb8-bee5-e10cd567a700/public" //Tao_ourPhilosophy_1.webp
//                   alt="Living the Tao Life"
//                   className="w-[60%] max-w-[500px] xl:max-w-[580px] 2xl:max-w-[700px] h-auto object-cover object-center"
//                 />
//               </div>
//             </div>
//           </section>

//           {/* ---- Section 3 ---- */}
//           <section className="relative w-screen h-screen overflow-hidden bg-[#f5f2f0]">
//             <img
//               src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/60fbafe1-a470-41d4-e20d-2a333e043100/public" //Tao_ourPhilosophy_2.webp
//               alt="Harmony with Nature"
//               className="w-full h-full object-cover object-center"
//             />

//             <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-[#948A78] text-[13px] tracking-[0.25em] uppercase">
//               in silence, everything speaks
//             </div>
//           </section>

//           {/* section 4 */}
//           <section
//             ref={addToRefs}
//             className="w-screen min-h-screen flex flex-col lg:flex-row bg-[#f5f2f0] overflow-hidden"
//           >
//             <div className="flex-1 flex justify-center items-center">
//               <img
//                 src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/dcb5080d-643e-44c1-b09c-6e6b0e678100/public" //Tao_ourPhilosophy_3.webp
//                 alt="Minimalist cream-colored interior with arched doorways and wooden staircase"
//                 className="w-[55vh] h-[82vh] object-cover object-center lg:ml-10 xl:ml-32"
//               />
//             </div>

//             <div className="flex-1 flex flex-col justify-center items-center text-center px-8 lg:px-16 xl:px-24 space-y-10 text-[#948A78]">
//               <div className="flex justify-center">
//                 <img
//                   src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/f760065d-4b4a-48e7-31ad-42592c8a2400/public" //Tao_ourPhilosophy_4.webp
//                   alt="Close-up of a circular window with flowing curtains and a small table"
//                   className="w-[48vh] h-[35vh] object-cover object-center mb-14 2xl:mb-32 xl:ml-20 lg:ml-24"
//                 />
//               </div>

//               <div className="flex flex-col items-start  2xl:mr-80">
//                 <h2 className="text-[20px] tracking-wide mb-2 text-right  -ml-36">
//                   The Tao <br /> State of Mind
//                 </h2>
//                 <div className="text-left leading-relaxed text-[13px] space-y-3 w-[250px]">
//                   <p>More than a stay — a sensory pause. </p>
//                   <p>A home to feel restored, grounded, and present.</p>
//                 </div>
//               </div>
//             </div>
//           </section>
//           {/* <section className="w-screen min-h-screen flex flex-col lg:flex-row bg-[#f5f2f0] overflow-hidden">

//             <div className="flex-1 flex justify-center items-center">
//               <img
//                 src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/dcb5080d-643e-44c1-b09c-6e6b0e678100/public" //Tao_ourPhilosophy_3.webp
//                 alt="Minimalist cream-colored interior with arched doorways and wooden staircase"
//                 className="w-[55vh] h-[82vh] object-cover object-center lg:ml-14 xl:ml-32"
//               />
//             </div>

//             <div className="flex-1 flex flex-col justify-center items-center text-center px-8 lg:px-16 xl:px-24 space-y-10 text-[#948A78]">

//               <div className="flex justify-center">
//                 <img
//                   src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/f760065d-4b4a-48e7-31ad-42592c8a2400/public" //Tao_ourPhilosophy_4.webp
//                   alt="Close-up of a circular window with flowing curtains and a small table"
//                   className="w-[48vh] h-[35vh] object-cover object-center mb-14 2xl:mb-32 xl:ml-20"
//                 />
//               </div>

//               <div className="flex flex-col items-end text-right">
//                 <h2 className="text-[20px] tracking-wide mb-2 lg:mr-[55vh] xl:mr-[85vh] 2xl:mr-[70vh] w-[200px]">
//                   The Tao <br /> State of Mind
//                 </h2>
//                 <div className="text-left leading-relaxed text-[13px] space-y-3 lg:mr-32 xl:mr-[45vh] 2xl:mr-[50vh] lg:w-[200px] 2xl:w-[250px] xl:w-[250px]">
//                   <p>More than a stay — a sensory pause. </p>
//                   <p>A home to feel restored, grounded, and present.</p>
//                 </div>
//               </div>
//             </div>
//           </section> */}

//           {/* section 5 */}
//           <section
//             ref={addToRefs}
//             className="meet-host-section-container w-full min-h-screen bg-[#f5f2f0] -mt-10 flex flex-col items-end justify-center px-6 py-16 lg:px-20 text-[#948A78]"
//           >
//             <div className="justify-center xl:mr-[70vh] lg:mr-[60vh]">
//               <h2 className="text-[13px] mb-12 w-full lg:w-auto text-left xl:mr-60 lg:mr-44 2xl:mr-[35vh] 4k:mr-[15vh] tracking-[0.25em]">
//                 MEET YOUR HOST
//               </h2>
//             </div>

//             <div className="hosts-profiles-wrapper flex flex-col lg:flex-row items-end justify-end gap-14 w-full max-w-6xl xl:mr-20 2xl:mr-40">
//               <div className="host-profile flex flex-col items-end text-right space-y-6">
//                 <div className="host-image-container overflow-hidden">
//                   <img
//                     src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/99f8a947-80d5-4cdc-159b-e69b04dbdc00/public"
//                     alt="Parth Thaker"
//                     className="host-image xl:w-[55vh] xl:h-[35vh] 2xl:w-[60vh] 2xl:h-[40vh] lg:w-[55vh] lg:h-[35vh] object-cover"
//                   />
//                 </div>
//                 <div className="space-y-2 text-[13px] w-[300px] lg:w-[300px] text-left xl:-mr-6 lg:-mr-6 2xl:-mr-5">
//                   <p>
//                     Parth Thaker and Vimal started with just two apartments in
//                     Goa back in 2017, driven by a shared love for Goa and
//                     everything it offers.
//                   </p>
//                   <p>
//                     Parth brings a deep passion for hospitality, always looking
//                     for ways to make every guest feel at home.
//                   </p>
//                 </div>
//               </div>

//               <div className="host-profile flex flex-col items-end text-right space-y-6">
//                 <div className="host-image-container overflow-hidden">
//                   <img
//                     src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/ec1cccbd-2d30-47ec-11d4-d7132f159600/public"
//                     alt="Vimal smiling with arms crossed"
//                     className="host-image w-[55vh] h-[35vh] 2xl:w-[60vh] 2xl:h-[40vh] lg:w-[55vh] lg:h-[35vh] object-cover"
//                   />
//                 </div>
//                 <p className="text-[13px] py-1 w-[270px] text-left xl:-mr-2 lg:-mr-3 2xl:-mr-1">
//                   Vimal, with his eye for detail and marketing skills, brings
//                   Goa to people long before they arrive. Together, they’ve grown
//                   Soham Villas with care, creating spaces that feel personal,
//                   peaceful and true to the spirit of Goa.
//                 </p>
//               </div>
//             </div>
//           </section>

//           {/* section 6 */}
//           <section
//             ref={addToRefs}
//             className="soham-villas-container -mt-10 py-16 px-6 bg-[#f5f2f0] text-[#948A78]"
//           >
//             <div className="soham-villas-main-content flex flex-col lg:flex-row items-stretch gap-12 4k:gap-[18rem] max-w-6xl mx-auto">
//               <div className="flex-1 flex flex-col justify-between items-start xl:ml-5 lg:ml-10 4k:-ml-[20vh] space-y-4">
//                 <div className="text-left w-[350px] xl:w-[370px]">
//                   <p className="text-[13px]">
//                     House of Tāo is brought to you by Soham Villas, a boutique
//                     hospitality brand known for its design-forward stays and
//                     thoughtful guest experiences. Inspired by the quiet beauty
//                     of Parra village, this villa was envisioned as a sanctuary
//                     where time moves at your pace.
//                   </p>
//                 </div>

//                 <div>
//                   <img
//                     src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/9312407f-7eba-4d43-c4ec-5fcea0005b00/public" //Tao_ourPhilosophy_7.webp
//                     alt="Soham Villas"
//                     className="w-[50vh] h-[78vh] object-cover object-top"
//                   />
//                 </div>
//               </div>

//               <div className="flex-1 flex flex-col justify-end xl:-mb-12 lg:-mb-10">
//                 <h2 className="text-[20px] xl:-ml-28 lg:-ml-20 2xl:-ml-5 mb-6">
//                   About Soham Villas
//                 </h2>

//                 <div className="text-[13px] xl:w-[350px] xl:ml-20 lg:ml-10 lg:w-[400px] space-y-4 mb-6">
//                   <p>
//                     Just like you - we came to Goa, but could never leave. Since
//                     then it’s the journey of making your Goa experience as
//                     holistic as it was ours.
//                   </p>
//                   <p>
//                     These detoxing experiences led us to create something
//                     permanent here - what first began with a few apartments has
//                     now developed elegantly into a collection of carefully
//                     crafted villas all across the breathtaking landscapes of
//                     Goa.
//                   </p>
//                   <p>
//                     We take immense pride and joy for all the exceptional
//                     memories we could be a part of, in the course of the last 5
//                     years.
//                   </p>
//                   <p>
//                     House of Tao is one of our most personal projects yet,
//                     rooted in simplicity, nature and slow living. It’s built not
//                     just to stay, but to feel. And we’re grateful to share that
//                     feeling with you.
//                   </p>
//                 </div>

//                 <a
//                   href="/BookNow"
//                   className="px-6 py-12 xl:ml-14 text-[13px] hover:font-bold underline decoration-[#948A78] underline-offset-[10px] decoration-1 hover:underline-offset-[5px] transition-all duration-300 inline-block text-[#948A78]"
//                 >
//                   BOOK YOUR STAY
//                 </a>
//               </div>
//             </div>
//           </section>
//           <NewFooter />
//         </div>
//       </div>

//       {/* =============== MOBILE VIEW ============== */}
//       <div className="flex flex-col lg:hidden text-center relative overflow-x-hidden bg-[#f5f2f0]">
//         {/* ---- Section 1 ---- */}
//         <div
//           className="fixed inset-0 w-full h-full bg-cover bg-center z-0"
//           style={{
//             backgroundImage: `url('https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/cf7e7b2c-2788-41f5-86a3-2a810fed7a00/public')`,
//           }}
//         >
//           <div className="absolute inset-0 bg-black/50"></div>
//           <div className="absolute inset-0 flex flex-col items-center justify-center text-[14px] tracking-[0.25em] uppercase text-[#F5F2F0] leading-relaxed">
//             <Typewriter
//               options={{
//                 delay: 60,
//                 deleteSpeed: 30,
//               }}
//               onInit={(typewriter) => {
//                 typewriter
//                   .typeString("get lost.")
//                   .pauseFor(800)
//                   .typeString("<br/>feel found.")
//                   .callFunction(() => {
//                     const cursor = document.querySelector(
//                       ".Typewriter__cursor"
//                     );
//                     if (cursor) cursor.style.display = "none";
//                   })
//                   .start();
//               }}
//             />
//           </div>
//         </div>

//         <div className="relative z-10 pt-[100vh]">
//           {/* header */}
//           <NewHeader />
//           {/* ---- Section 2 ---- */}
//           <section
//             ref={addToRefs}
//             className="w-full min-h-[100vh] flex flex-col items-center justify-center py-28 space-y-8 px-6 bg-[#f5f2f0]"
//           >
//             <div className="text-left text-[13px] space-y-3 text-[#948A78] ml-20 sm:ml-[50vh] sm:w-[250px]">
//               <p>
//                 At House of Tāo, hospitality is not just about where you stay —
//                 it’s about how you feel.
//               </p>
//               <p>
//                 This is more than a villa; it’s an intentional space that lets
//                 you slow down, breathe deeply, and connect with what matters.
//               </p>
//               <p>
//                 Every corner carries a story — from the locally sourced
//                 materials to the soft, grounding textures that make you feel at
//                 home the moment you arrive.
//               </p>
//             </div>

//             <img
//               src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/0eaf71e3-77d4-4eb8-bee5-e10cd567a700/public" //Tao_ourPhilosophy_1.webp
//               alt="Living the Tao Life"
//               className="w-[35vh] h-[52vh] sm:w-[39vh] sm:h-[50vh] object-cover sm:mr-64 mr-24"
//             />

//             <div className="flex flex-col items-end text-[#948A78] mt-5 ml-[20vh] w-full">
//               <h2 className="text-[20px] tracking-wide text-right mb-2 mr-56 sm:mr-80 w-[100px]">
//                 Living the <br />
//                 Tao Life
//               </h2>

//               <div className="leading-relaxed text-[13px] space-y-3 text-left sm:w-[200px] w-[200px] mr-20 sm:mr-32">
//                 <p>Choose ease over effort, presence over pace.</p>
//                 <p>
//                   Let the day unfold without urgency, rise with the sun, walk
//                   barefoot, listen to the wind move through the trees.
//                 </p>
//                 <p>
//                   Nothing here demands your attention, yet everything invites
//                   it. In this quiet rhythm, you begin to notice more, not just
//                   around you, but within you.
//                 </p>
//               </div>

//               <div className="mt-5 -mr-1 sm:-mr-44 w-full flex justify-center">
//                 <img
//                   src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/f760065d-4b4a-48e7-31ad-42592c8a2400/public" //Tao_ourPhilosophy_4.webp
//                   alt="Nature View 2"
//                   className="w-[41vh] sm:w-[50vh] object-cover"
//                 />
//               </div>
//             </div>
//           </section>
//           {/* ---- Section 3 ---- */}
//           <section
//             ref={addToRefs}
//             className="relative w-screen h-[60vh] overflow-hidden bg-[#f5f2f0]"
//           >
//             <img
//               src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/dcb5080d-643e-44c1-b09c-6e6b0e678100/public"
//               alt="Nature View 3"
//               className="sm:w-[50vh] sm:h-[65vh] h-[60vh] w-[40vh] object-cover object-center ml-3 sm:ml-10"
//             />
//           </section>
//           {/* ---- Spacer ---- */}
//           <div className="w-full h-20 bg-[#f5f2f0]"></div> {/* visible space */}
//           {/* ---- Section 4 ---- */}
//           <section
//             ref={addToRefs}
//             className="hosts-section py-5 px-6 bg-[#f5f2f0] text-[#948A78]"
//           >
//             <h2 className="section-title text-[13px] text-left sm:ml-3 ml-3 mb-6 uppercase tracking-[0.25em]">
//               Meet Your Hosts
//             </h2>

//             <p className="text-[13px] text-left w-[250px] sm:ml-3 ml-3 mb-12">
//               Parth Thaker and Vimal started with just two apartments in Goa
//               back in 2017, driven by a shared love for Goa and everything it
//               offers.
//             </p>

//             <div className="hosts-profiles flex flex-col lg:flex-row gap-12 justify-center items-start text-[13px]">
//               {/* Parth */}
//               <div className="host-profile flex-1 items-end">
//                 <img
//                   src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/99f8a947-80d5-4cdc-159b-e69b04dbdc00/public"
//                   alt="Parth Thaker"
//                   className="host-image sm:w-[33vh] sm:h-[42vh] sm:ml-[60vh] w-[33vh] h-[42vh] ml-[14vh] object-cover mx-auto lg:mx-0 mb-4"
//                 />
//                 <p className="host-description text-left sm:w-[300px] sm:ml-[60vh] mt-8 ml-20">
//                   Parth brings a deep passion for hospitality, always looking
//                   for ways to make every guest feel at home.
//                 </p>
//               </div>

//               {/* Vimal */}
//               <div className="host-profile flex-1 items-end">
//                 <img
//                   src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/ec1cccbd-2d30-47ec-11d4-d7132f159600/public"
//                   alt="Vimal"
//                   className="host-image sm:w-[33vh] sm:h-[42vh] sm:ml-[60vh] w-[33vh] h-[42vh] ml-[14vh] object-cover mx-auto lg:mx-0 mb-4"
//                 />
//                 <p className="host-description text-left sm:w-[300px] sm:ml-[60vh] mt-8 ml-20">
//                   Vimal, with his eye for detail and marketing skills, brings
//                   Goa to people long before they arrive. Together, they’ve grown
//                   Soham Villas with care, creating spaces that feel personal,
//                   peaceful and true to the spirit of Goa.
//                 </p>
//               </div>
//             </div>
//           </section>
//           {/* section 5 */}
//           <section
//             ref={addToRefs}
//             className="relative w-full bg-[#f5f2f0] py-20 flex flex-col lg:flex-row justify-between items-start px-5 lg:px-20"
//           >
//             {/* Left Column - Host Profile */}
//             <div className="host-profile flex-1 flex flex-col items-start text-[#948A78] text-[13px]">
//               <p className="host-description text-left sm:w-[400px] sm:ml-[10vh] mt-8 mb-5 ml-5 mr-20">
//                 House of Tāo is brought to you by Soham Villas, a boutique
//                 hospitality brand known for its design-forward stays and
//                 thoughtful guest experiences. Inspired by the quiet beauty of
//                 Parra village, this villa was envisioned as a sanctuary where
//                 time moves at your pace.
//               </p>

//               <img
//                 src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/9312407f-7eba-4d43-c4ec-5fcea0005b00/public" // Tao_ourPhilosophy_7.webp
//                 alt="about"
//                 className="host-image sm:w-[40vh] sm:h-[62vh] sm:ml-[10vh] w-[35vh] h-[50vh] ml-[4vh] object-cover mx-auto lg:mx-0 mb-4"
//               />
//             </div>

//             {/* Right Column - About Soham Villas */}
//             <div className="flex-1 flex flex-col items-start lg:items-end text-[#948A78]">
//               <h2 className="text-right sm:ml-[30vh] -ml-14 mt-10 mb-5 text-[#948A78] text-[20px] w-[200px]">
//                 About <br />
//                 Soham Villas
//               </h2>

//               <div className="text-left sm:w-[350px] w-[200px] ml-[12vh] sm:space-y-5 space-y-3 text-[13px] sm:ml-[63vh]">
//                 <p>
//                   Just like you - we came to Goa, but could never leave. Since
//                   then it’s the journey of making your Goa experience as
//                   holistic as it was ours.
//                 </p>

//                 <p>
//                   These detoxing experiences led us to create something
//                   permanent here - what first began with a few apartments has
//                   now developed elegantly into a collection of carefully crafted
//                   villas all across the breathtaking landscapes of Goa.
//                 </p>

//                 <p>
//                   We take immense pride and joy for all the exceptional memories
//                   we could be a part of, in the course of the last 5 years.
//                 </p>

//                 <p>
//                   House of Tao is one of our most personal projects yet, rooted
//                   in simplicity, nature and slow living. It’s built not just to
//                   stay, but to feel. And we’re grateful to share that feeling
//                   with you.
//                 </p>

//                 <a
//                   href="/BookNow"
//                   className="px-6 py-2 -ml-5 text-[13px] hover:font-bold underline decoration-[#948A78] underline-offset-[10px] decoration-1 hover:underline-offset-[5px] transition-all duration-300 mb-6 inline-block text-[#948A78]"
//                 >
//                   BOOK&nbsp;YOUR&nbsp;STAY
//                 </a>
//               </div>
//             </div>
//           </section>
//           <NewFooter />
//         </div>
//       </div>
//     </div>
//   );
// };

// export default OurPhilosophy;
